import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useAppsT } from '@/lib/appsI18n';
import AppShell from '@/pages/client/apps/components/AppShell';
import { useAppData, localId, formatShortDate } from '@/lib/appStorage';

type Priority = 'low' | 'normal' | 'high';
type TaskStatus = 'todo' | 'done';
type EventType = 'cours' | 'examen' | 'rdv' | 'revision';

interface Task {
  id: string;
  title: string;
  description: string;
  subject: string;
  due: string;
  priority: Priority;
  status: TaskStatus;
  estimate: number;
}

interface PlannerEvent {
  id: string;
  title: string;
  date: string;
  time: string;
  type: EventType;
  repeat: 'none' | 'week';
}

interface TasksState {
  tasks: Task[];
  events: PlannerEvent[];
}

const todayIso = () => new Date().toISOString().slice(0, 10);
const shiftDays = (iso: string, days: number) => {
  const date = new Date(`${iso}T12:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
};

const EMPTY_STATE: TasksState = { tasks: [], events: [] };

const PRIORITY_TONE: Record<Priority, string> = {
  high: 'bg-primary-100 text-primary-800',
  normal: 'bg-background-100 text-foreground-600',
  low: 'bg-secondary-100 text-secondary-900',
};

const EVENT_TONE: Record<EventType, string> = {
  cours: 'bg-secondary-100 text-secondary-900',
  examen: 'bg-primary-100 text-primary-800',
  rdv: 'bg-background-200 text-foreground-800',
  revision: 'bg-accent-100 text-accent-900',
};

export default function ZifekTasksPage() {
  const { user } = useAuth();
  const { t, isRtl } = useAppsT();
  const storage = useAppData<TasksState>('client_tasks_v2', EMPTY_STATE, { scope: 'user', userId: user?.id });
  const { data, setData, ready } = storage;

  const [view, setView] = useState<'tasks' | 'day' | 'week' | 'month'>('tasks');
  const [cursor, setCursor] = useState(todayIso());
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [reminderState, setReminderState] = useState<'unknown' | 'granted' | 'denied'>('unknown');
  const [notice, setNotice] = useState<string | null>(null);

  const [taskDraft, setTaskDraft] = useState({
    title: '',
    description: '',
    subject: '',
    due: todayIso(),
    priority: 'normal' as Priority,
    estimate: 60,
  });
  const [eventDraft, setEventDraft] = useState({
    title: '',
    date: todayIso(),
    time: '09:00',
    type: 'cours' as EventType,
    repeat: 'none' as 'none' | 'week',
  });

  useEffect(() => {
    if (typeof Notification !== 'undefined') {
      setReminderState(
        Notification.permission === 'granted' ? 'granted' : Notification.permission === 'denied' ? 'denied' : 'unknown',
      );
    }
  }, []);

  const tasks = data.tasks;
  const events = data.events;

  const overdue = useMemo(() => tasks.filter((task) => task.status === 'todo' && task.due < todayIso()), [tasks]);
  const upcoming = useMemo(
    () =>
      tasks
        .filter((task) => task.status === 'todo' && task.due >= todayIso())
        .sort((a, b) => a.due.localeCompare(b.due))
        .slice(0, 5),
    [tasks],
  );

  const addTask = () => {
    if (!taskDraft.title.trim()) return;
    const task: Task = {
      id: localId('task'),
      title: taskDraft.title.trim(),
      description: taskDraft.description.trim(),
      subject: taskDraft.subject.trim(),
      due: taskDraft.due,
      priority: taskDraft.priority,
      status: 'todo',
      estimate: Number(taskDraft.estimate) || 60,
    };
    setData((prev) => ({ ...prev, tasks: [task, ...prev.tasks] }));
    setTaskDraft({ title: '', description: '', subject: '', due: todayIso(), priority: 'normal', estimate: 60 });
    setShowTaskForm(false);
  };

  const addEvent = () => {
    if (!eventDraft.title.trim()) return;
    const event: PlannerEvent = {
      id: localId('evt'),
      title: eventDraft.title.trim(),
      date: eventDraft.date,
      time: eventDraft.time,
      type: eventDraft.type,
      repeat: eventDraft.repeat,
    };
    setData((prev) => ({ ...prev, events: [event, ...prev.events] }));
    setEventDraft({ title: '', date: cursor, time: '09:00', type: 'cours', repeat: 'none' });
    setShowEventForm(false);
  };

  const toggleTask = (id: string) => {
    setData((prev) => ({
      ...prev,
      tasks: prev.tasks.map((task) =>
        task.id === id ? { ...task, status: task.status === 'done' ? 'todo' : 'done' } : task,
      ),
    }));
  };

  const removeTask = (id: string) => {
    setData((prev) => ({ ...prev, tasks: prev.tasks.filter((task) => task.id !== id) }));
  };

  const removeEvent = (id: string) => {
    setData((prev) => ({ ...prev, events: prev.events.filter((event) => event.id !== id) }));
  };

  const askReminders = async () => {
    if (typeof Notification === 'undefined') {
      setNotice('Les notifications ne sont pas disponibles sur cet appareil.');
      return;
    }
    const permission = await Notification.requestPermission();
    setReminderState(permission === 'granted' ? 'granted' : 'denied');
    if (permission === 'granted') {
      setNotice('Rappels activés. Ils fonctionnent même sans connexion, tant que l’application est ouverte.');
      const next = upcoming[0];
      if (next) {
        const target = new Date(`${next.due}T09:00:00`).getTime() - Date.now();
        if (target > 0 && target < 60 * 60 * 1000) {
          window.setTimeout(() => {
            new Notification('Zifek Tasks', { body: `À faire : ${next.title}` });
          }, target);
        }
      }
    } else {
      setNotice('Les rappels restent désactivés. Vous pouvez les autoriser dans les réglages du navigateur.');
    }
  };

  const weekDays = useMemo(() => {
    const base = new Date(`${cursor}T12:00:00`);
    const weekday = (base.getDay() + 6) % 7;
    const start = shiftDays(cursor, -weekday);
    return Array.from({ length: 7 }, (_, i) => shiftDays(start, i));
  }, [cursor]);

  const monthCells = useMemo(() => {
    const base = new Date(`${cursor}T12:00:00`);
    const first = new Date(base.getFullYear(), base.getMonth(), 1);
    const offset = (first.getDay() + 6) % 7;
    const start = shiftDays(first.toISOString().slice(0, 10), -offset);
    return Array.from({ length: 42 }, (_, i) => shiftDays(start, i));
  }, [cursor]);

  const eventsOn = (date: string) => events.filter((event) => event.date === date);
  const tasksOn = (date: string) => tasks.filter((task) => task.due === date);

  const eventLabel = (type: EventType) =>
    ({ cours: t('tasks.course'), examen: t('tasks.exam'), rdv: t('tasks.appointment'), revision: t('tasks.revision') })[type];

  const priorityLabel = (priority: Priority) =>
    ({ low: t('tasks.low'), normal: t('tasks.normal'), high: t('tasks.high') })[priority];

  const renderDayCell = (date: string) => {
    const isToday = date === todayIso();
    const dayEvents = eventsOn(date);
    const dayTasks = tasksOn(date);
    return (
      <button
        key={date}
        onClick={() => setCursor(date)}
        className={`text-left rounded-md border p-1.5 min-h-[64px] transition-colors cursor-pointer ${
          isToday ? 'border-accent-300 bg-accent-50/60' : 'border-background-200/60 bg-background-50 hover:bg-background-100/60'
        }`}
      >
        <div className="flex items-center justify-between">
          <span className={`text-[10px] font-semibold ${isToday ? 'text-accent-800' : 'text-foreground-700'}`}>
            {new Date(`${date}T12:00:00`).getDate()}
          </span>
          {dayTasks.length > 0 && (
            <span className="text-[8px] px-1 rounded-full bg-secondary-100 text-secondary-900 font-semibold">
              {dayTasks.length}
            </span>
          )}
        </div>
        <div className="mt-0.5 space-y-0.5">
          {dayEvents.slice(0, 1).map((event) => (
            <p key={event.id} className="text-[8px] text-foreground-600 truncate">
              {event.time} {event.title}
            </p>
          ))}
          {dayEvents.length > 1 && <p className="text-[8px] text-foreground-400">+{dayEvents.length - 1}</p>}
        </div>
      </button>
    );
  };

  const statCards = [
    { label: t('tasks.todo'), value: tasks.filter((task) => task.status === 'todo').length, icon: 'ri-checkbox-blank-circle-line', tone: 'bg-background-100 text-foreground-700' },
    { label: t('tasks.overdue'), value: overdue.length, icon: 'ri-alarm-warning-line', tone: 'bg-accent-100 text-accent-900' },
    { label: t('tasks.done'), value: tasks.filter((task) => task.status === 'done').length, icon: 'ri-checkbox-circle-line', tone: 'bg-secondary-100 text-secondary-900' },
    { label: t('tasks.events'), value: events.length, icon: 'ri-calendar-event-line', tone: 'bg-primary-100 text-primary-800' },
  ];

  return (
    <AppShell
      slug="zifek-tasks"
      sync={{
        offline: storage.offline,
        syncing: storage.syncing,
        pending: storage.pending,
        syncError: storage.syncError,
        onSync: storage.forceSync,
      }}
      headerExtra={
        <button
          onClick={askReminders}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium whitespace-nowrap cursor-pointer transition-colors ${
            reminderState === 'granted'
              ? 'bg-accent-100 text-accent-900'
              : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
          }`}
        >
          <i className={reminderState === 'granted' ? 'ri-notification-3-line' : 'ri-notification-off-line'}></i>
          <span className="hidden sm:inline">
            {reminderState === 'granted' ? t('tasks.remindersOn') : t('tasks.reminders')}
          </span>
        </button>
      }
    >
      {!ready ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
        </div>
      ) : (
        <div className={`space-y-4 ${isRtl ? 'text-right' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
          {notice && (
            <div className="flex items-start gap-2 px-3.5 py-2.5 rounded-lg bg-secondary-100/70 text-secondary-900 text-xs">
              <i className="ri-information-line mt-0.5"></i>
              <span className="flex-1">{notice}</span>
              <button onClick={() => setNotice(null)} className="cursor-pointer" title={t('app.close')}>
                <i className="ri-close-line"></i>
              </button>
            </div>
          )}

          {/* Statistiques compactes */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
            {statCards.map((stat) => (
              <div key={stat.label} className="bg-background-50 border border-background-200/60 rounded-lg p-3 flex items-center gap-2.5">
                <span className={`w-8 h-8 rounded-md ${stat.tone} flex items-center justify-center flex-shrink-0`}>
                  <i className={`${stat.icon} text-sm`}></i>
                </span>
                <div className="min-w-0">
                  <p className="text-base font-bold text-foreground-950 leading-none">{stat.value}</p>
                  <p className="text-[10px] text-foreground-500 mt-0.5 truncate">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Barre d'outils */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
            <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-1 w-fit">
              {[
                { key: 'tasks', label: t('tasks.tasks'), icon: 'ri-layout-grid-line' },
                { key: 'day', label: t('tasks.day'), icon: 'ri-sun-line' },
                { key: 'week', label: t('tasks.week'), icon: 'ri-calendar-2-line' },
                { key: 'month', label: t('tasks.month'), icon: 'ri-calendar-line' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setView(tab.key as typeof view)}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-medium whitespace-nowrap cursor-pointer transition-colors ${
                    view === tab.key ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  <i className={tab.icon}></i>
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowTaskForm((v) => !v)}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-foreground-950 text-background-50 text-[11px] font-semibold whitespace-nowrap cursor-pointer hover:bg-foreground-800"
              >
                <i className="ri-add-line"></i>
                {t('tasks.newTask')}
              </button>
              <button
                onClick={() => setShowEventForm((v) => !v)}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-background-100 text-foreground-700 text-[11px] font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70"
              >
                <i className="ri-calendar-event-line"></i>
                {t('tasks.newEvent')}
              </button>
            </div>
          </div>

          {/* Formulaire tâche */}
          {showTaskForm && (
            <div className="bg-background-50 border border-background-200/60 rounded-xl p-3.5 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <input
                value={taskDraft.title}
                onChange={(e) => setTaskDraft({ ...taskDraft, title: e.target.value })}
                placeholder={t('tasks.newTask')}
                className="sm:col-span-2 px-3 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-foreground-300"
              />
              <textarea
                value={taskDraft.description}
                onChange={(e) => setTaskDraft({ ...taskDraft, description: e.target.value })}
                rows={2}
                placeholder={t('tasks.details')}
                className="sm:col-span-2 px-3 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-foreground-300 resize-y"
              />
              <input
                value={taskDraft.subject}
                onChange={(e) => setTaskDraft({ ...taskDraft, subject: e.target.value })}
                placeholder={t('notes.subject')}
                className="px-3 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-foreground-300"
              />
              <input
                type="date"
                value={taskDraft.due}
                onChange={(e) => setTaskDraft({ ...taskDraft, due: e.target.value })}
                className="px-3 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-foreground-300"
              />
              <select
                value={taskDraft.priority}
                onChange={(e) => setTaskDraft({ ...taskDraft, priority: e.target.value as Priority })}
                className="px-3 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-foreground-300 cursor-pointer"
              >
                {(['low', 'normal', 'high'] as Priority[]).map((priority) => (
                  <option key={priority} value={priority}>
                    {t('tasks.priority')} · {priorityLabel(priority)}
                  </option>
                ))}
              </select>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={5}
                  step={5}
                  value={taskDraft.estimate}
                  onChange={(e) => setTaskDraft({ ...taskDraft, estimate: Number(e.target.value) })}
                  className="w-20 px-3 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-foreground-300"
                />
                <span className="text-[11px] text-foreground-500 flex-1">{t('tasks.estimate')}</span>
                <button
                  onClick={addTask}
                  className="px-4 py-2.5 rounded-full bg-accent-500 text-background-50 text-[11px] font-semibold whitespace-nowrap cursor-pointer hover:bg-accent-600"
                >
                  {t('app.add')}
                </button>
              </div>
            </div>
          )}

          {/* Formulaire événement */}
          {showEventForm && (
            <div className="bg-background-50 border border-background-200/60 rounded-xl p-3.5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
              <input
                value={eventDraft.title}
                onChange={(e) => setEventDraft({ ...eventDraft, title: e.target.value })}
                placeholder={t('tasks.newEvent')}
                className="lg:col-span-2 px-3 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-foreground-300"
              />
              <select
                value={eventDraft.type}
                onChange={(e) => setEventDraft({ ...eventDraft, type: e.target.value as EventType })}
                className="px-3 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-foreground-300 cursor-pointer"
              >
                {(['cours', 'examen', 'rdv', 'revision'] as EventType[]).map((type) => (
                  <option key={type} value={type}>
                    {eventLabel(type)}
                  </option>
                ))}
              </select>
              <input
                type="date"
                value={eventDraft.date}
                onChange={(e) => setEventDraft({ ...eventDraft, date: e.target.value })}
                className="px-3 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-foreground-300"
              />
              <input
                type="time"
                value={eventDraft.time}
                onChange={(e) => setEventDraft({ ...eventDraft, time: e.target.value })}
                className="px-3 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-foreground-300"
              />
              <select
                value={eventDraft.repeat}
                onChange={(e) => setEventDraft({ ...eventDraft, repeat: e.target.value as 'none' | 'week' })}
                className="px-3 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-foreground-300 cursor-pointer"
              >
                <option value="none">{t('tasks.once')}</option>
                <option value="week">{t('tasks.weekly')}</option>
              </select>
              <button
                onClick={addEvent}
                className="px-4 py-2.5 rounded-full bg-accent-500 text-background-50 text-[11px] font-semibold whitespace-nowrap cursor-pointer hover:bg-accent-600 lg:col-span-2"
              >
                {t('tasks.newEvent')}
              </button>
            </div>
          )}

          {/* Contenu */}
          {view === 'tasks' ? (
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_290px] gap-4 items-start">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500">{t('tasks.tasks')}</h3>
                  <span className="text-[11px] text-foreground-400">{tasks.length}</span>
                </div>
                {tasks.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 px-6 text-center border border-dashed border-background-300/70 rounded-xl">
                    <i className="ri-task-line text-2xl text-foreground-300"></i>
                    <p className="text-xs text-foreground-500 mt-2">{t('tasks.noTasks')}</p>
                    <button
                      onClick={() => setShowTaskForm(true)}
                      className="mt-3 px-4 py-2 rounded-full bg-foreground-950 text-background-50 text-[11px] font-medium whitespace-nowrap cursor-pointer"
                    >
                      {t('tasks.newTask')}
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5">
                    {[...tasks]
                      .sort(
                        (a, b) =>
                          Number(a.status === 'done') - Number(b.status === 'done') || a.due.localeCompare(b.due),
                      )
                      .map((task) => {
                        const late = task.status === 'todo' && task.due < todayIso();
                        const done = task.status === 'done';
                        return (
                          <article
                            key={task.id}
                            className={`group relative flex flex-col rounded-lg border p-3 min-h-[124px] transition-colors ${
                              done
                                ? 'border-background-200/50 bg-background-100/40'
                                : late
                                  ? 'border-accent-300/70 bg-accent-50/40'
                                  : 'border-background-200/60 bg-background-50 hover:border-foreground-300/60'
                            }`}
                          >
                            <div className="flex items-start gap-2">
                              <button
                                onClick={() => toggleTask(task.id)}
                                className="mt-0.5 flex-shrink-0 cursor-pointer"
                                title={done ? t('tasks.todo') : t('tasks.done')}
                              >
                                <i
                                  className={`text-base ${
                                    done
                                      ? 'ri-checkbox-circle-fill text-accent-600'
                                      : 'ri-checkbox-blank-circle-line text-foreground-300'
                                  }`}
                                ></i>
                              </button>
                              <p
                                className={`text-xs font-semibold leading-snug flex-1 line-clamp-2 ${
                                  done ? 'line-through text-foreground-400' : 'text-foreground-900'
                                }`}
                              >
                                {task.title}
                              </p>
                              <button
                                onClick={() => removeTask(task.id)}
                                className="w-6 h-6 rounded flex items-center justify-center text-foreground-300 hover:text-red-600 hover:bg-red-50 cursor-pointer flex-shrink-0 opacity-0 group-hover:opacity-100"
                                title={t('app.delete')}
                              >
                                <i className="ri-close-line text-[11px]"></i>
                              </button>
                            </div>
                            {task.description && (
                              <p className="text-[10px] text-foreground-500 mt-1 line-clamp-2">{task.description}</p>
                            )}
                            <div className="mt-auto pt-2 flex flex-wrap items-center gap-1">
                              <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold whitespace-nowrap ${PRIORITY_TONE[task.priority]}`}>
                                {priorityLabel(task.priority)}
                              </span>
                              {late && (
                                <span className="px-1.5 py-0.5 rounded-full bg-accent-500 text-background-50 text-[9px] font-bold whitespace-nowrap">
                                  {t('tasks.overdue')}
                                </span>
                              )}
                              <span className="text-[9px] text-foreground-400 ml-auto whitespace-nowrap">
                                {formatShortDate(task.due)} · {task.estimate} min
                              </span>
                            </div>
                            {task.subject && <p className="text-[9px] text-foreground-400 mt-1 truncate">{task.subject}</p>}
                          </article>
                        );
                      })}
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div className="bg-background-50 border border-background-200/60 rounded-xl p-3.5">
                  <h3 className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mb-2">{t('tasks.upcoming')}</h3>
                  {upcoming.length === 0 ? (
                    <p className="text-[11px] text-foreground-400">{t('tasks.nothingSoon')}</p>
                  ) : (
                    <ul className="space-y-2">
                      {upcoming.map((task) => (
                        <li key={task.id} className="flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-accent-500 mt-1.5 flex-shrink-0"></span>
                          <div className="min-w-0">
                            <p className="text-[11px] font-medium text-foreground-800 truncate">{task.title}</p>
                            <p className="text-[10px] text-foreground-400">{formatShortDate(task.due)}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="bg-background-50 border border-background-200/60 rounded-xl overflow-hidden">
                  <div className="px-3.5 py-2.5 border-b border-background-200/50 flex items-center justify-between">
                    <h3 className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500">{t('tasks.events')}</h3>
                    <span className="text-[10px] text-foreground-400">{events.length}</span>
                  </div>
                  <ul className="max-h-72 overflow-y-auto divide-y divide-background-200/40">
                    {events.length === 0 && <li className="text-[11px] text-foreground-400 px-3.5 py-3">{t('tasks.noEvents')}</li>}
                    {[...events]
                      .sort((a, b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`))
                      .map((event) => (
                        <li key={event.id} className="flex items-start gap-2 px-3.5 py-2.5 group">
                          <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold whitespace-nowrap mt-0.5 ${EVENT_TONE[event.type]}`}>
                            {eventLabel(event.type)}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-medium text-foreground-800 truncate">{event.title}</p>
                            <p className="text-[10px] text-foreground-400">
                              {formatShortDate(event.date)} · {event.time}
                              {event.repeat === 'week' ? ' · 7j' : ''}
                            </p>
                          </div>
                          <button
                            onClick={() => removeEvent(event.id)}
                            className="opacity-0 group-hover:opacity-100 w-6 h-6 rounded flex items-center justify-center text-foreground-400 hover:text-red-600 cursor-pointer"
                            title={t('app.delete')}
                          >
                            <i className="ri-close-line text-[11px]"></i>
                          </button>
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-background-50 border border-background-200/60 rounded-xl p-3.5">
              <div className="flex items-center justify-between mb-3 gap-2">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() =>
                      setCursor(
                        view === 'day' ? shiftDays(cursor, -1) : view === 'week' ? shiftDays(cursor, -7) : shiftDays(cursor, -30),
                      )
                    }
                    className="w-8 h-8 rounded-full hover:bg-background-100 flex items-center justify-center text-foreground-500 cursor-pointer"
                    title="Précédent"
                  >
                    <i className={isRtl ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'}></i>
                  </button>
                  <button
                    onClick={() => setCursor(todayIso())}
                    className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-700 text-[11px] font-medium whitespace-nowrap cursor-pointer"
                  >
                    {t('tasks.today')}
                  </button>
                  <button
                    onClick={() =>
                      setCursor(
                        view === 'day' ? shiftDays(cursor, 1) : view === 'week' ? shiftDays(cursor, 7) : shiftDays(cursor, 30),
                      )
                    }
                    className="w-8 h-8 rounded-full hover:bg-background-100 flex items-center justify-center text-foreground-500 cursor-pointer"
                    title="Suivant"
                  >
                    <i className={isRtl ? 'ri-arrow-left-s-line' : 'ri-arrow-right-s-line'}></i>
                  </button>
                </div>
                <p className="text-xs font-semibold text-foreground-900 truncate">
                  {new Date(`${cursor}T12:00:00`).toLocaleDateString(isRtl ? 'ar' : 'fr-FR', {
                    month: 'long',
                    year: 'numeric',
                    day: view === 'month' ? undefined : 'numeric',
                  })}
                </p>
              </div>

              {view === 'day' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {eventsOn(cursor).length === 0 && tasksOn(cursor).length === 0 ? (
                    <p className="text-[11px] text-foreground-400 py-8 text-center col-span-full">{t('tasks.emptyDay')}</p>
                  ) : (
                    <>
                      {eventsOn(cursor)
                        .sort((a, b) => a.time.localeCompare(b.time))
                        .map((event) => (
                          <div key={event.id} className="flex items-center gap-2 p-2.5 rounded-lg bg-secondary-100/60">
                            <span className="text-[11px] font-bold text-secondary-900 w-11 flex-shrink-0">{event.time}</span>
                            <div className="min-w-0">
                              <p className="text-[11px] font-semibold text-foreground-900 truncate">{event.title}</p>
                              <p className="text-[10px] text-foreground-500">{eventLabel(event.type)}</p>
                            </div>
                          </div>
                        ))}
                      {tasksOn(cursor).map((task) => (
                        <div key={task.id} className="flex items-center gap-2 p-2.5 rounded-lg bg-background-100/70">
                          <i className="ri-checkbox-blank-circle-line text-foreground-400"></i>
                          <div className="min-w-0">
                            <p className="text-[11px] font-semibold text-foreground-900 truncate">{task.title}</p>
                            <p className="text-[10px] text-foreground-500">{task.estimate} min</p>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              ) : view === 'week' ? (
                <div className="grid grid-cols-7 gap-1">
                  {weekDays.map((date) => (
                    <div key={date} className="min-w-0">
                      <p className="text-[9px] font-semibold text-foreground-500 text-center mb-1">
                        {new Date(`${date}T12:00:00`).toLocaleDateString(isRtl ? 'ar' : 'fr-FR', { weekday: 'short' })}
                      </p>
                      {renderDayCell(date)}
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {(isRtl
                      ? ['إث', 'ثل', 'أر', 'خم', 'جم', 'سب', 'أح']
                      : ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']
                    ).map((day) => (
                      <p key={day} className="text-[9px] font-semibold text-foreground-500 text-center">
                        {day}
                      </p>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1">{monthCells.map((date) => renderDayCell(date))}</div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </AppShell>
  );
}